import React, { useEffect, useRef, useState } from "react";
import { Mic, Square, Loader2, Cpu, Chrome } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";

/**
 * Two STT engines:
 *  - "browser": Web Speech API (free, instant, browser-dependent)
 *  - "whisper": server-side Whisper (better accuracy for accents & noise)
 */
export default function VoiceControl({ onParsed }) {
  const [engine, setEngine] = useState("whisper");
  const [listening, setListening] = useState(false);
  const [transcript, setTranscript] = useState("");
  const [processing, setProcessing] = useState(false);
  const [manualText, setManualText] = useState("");
  const [browserSupported, setBrowserSupported] = useState(true);
  const recognitionRef = useRef(null);
  const mediaRef = useRef(null);
  const chunksRef = useRef([]);

  useEffect(() => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) { setBrowserSupported(false); return; }
    const rec = new SR();
    rec.lang = "it-IT";
    rec.continuous = false;
    rec.interimResults = true;
    rec.maxAlternatives = 1;
    rec.onresult = (e) => {
      let full = "";
      for (let i = 0; i < e.results.length; i++) full += e.results[i][0].transcript;
      setTranscript(full);
    };
    rec.onerror = (e) => { toast.error("Errore microfono: " + e.error); setListening(false); };
    rec.onend = () => setListening(false);
    recognitionRef.current = rec;
  }, []);

  const startBrowser = () => {
    setTranscript("");
    try { recognitionRef.current.start(); setListening(true); }
    catch { toast.error("Permesso microfono negato"); }
  };
  const stopBrowser = () => { recognitionRef.current?.stop(); setListening(false); };

  const startWhisper = async () => {
    setTranscript("");
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mime = MediaRecorder.isTypeSupported("audio/webm") ? "audio/webm" : "";
      const mr = new MediaRecorder(stream, mime ? { mimeType: mime } : undefined);
      chunksRef.current = [];
      mr.ondataavailable = (e) => { if (e.data.size > 0) chunksRef.current.push(e.data); };
      mr.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        const blob = new Blob(chunksRef.current, { type: mime || "audio/webm" });
        setProcessing(true);
        try {
          const fd = new FormData();
          fd.append("audio", blob, "voice.webm");
          const { data } = await api.post("/voice/transcribe", fd, { headers: { "Content-Type": "multipart/form-data" } });
          setTranscript(data.transcript || "");
          if (!data.transcript) toast.error("Nessuna voce rilevata");
        } catch (err) {
          toast.error(err?.response?.data?.detail || "Errore trascrizione");
        } finally {
          setProcessing(false);
        }
      };
      mr.start();
      mediaRef.current = mr;
      setListening(true);
    } catch (e) {
      toast.error("Permesso microfono negato");
    }
  };
  const stopWhisper = () => { mediaRef.current?.stop(); setListening(false); };

  const start = () => (engine === "browser" ? startBrowser() : startWhisper());
  const stop = () => (engine === "browser" ? stopBrowser() : stopWhisper());

  const parse = async (text) => {
    if (!text.trim()) { toast.error("Trascrizione vuota"); return; }
    setProcessing(true);
    try {
      const { data } = await api.post("/voice/parse", { transcript: text });
      onParsed(data, text);
      setTranscript(""); setManualText("");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore parsing AI");
    } finally { setProcessing(false); }
  };

  return (
    <div className="bg-white rounded-3xl border border-[#E8E2D5] p-6 sm:p-8 shadow-sm">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="text-xs uppercase tracking-wider text-[#6B625B] font-semibold">Comando Vocale</div>
          <h3 className="font-serif-display text-2xl font-bold mt-1">Parla al tuo menu</h3>
        </div>
        <div className={`px-3 py-1.5 rounded-full text-xs font-semibold ${listening ? "bg-[#FDECE4] text-[#C85A32]" : processing ? "bg-[#FFF7E6] text-[#8C580C]" : "bg-[#EAF0E9] text-[#3B503A]"}`}>
          {listening ? "In ascolto..." : processing ? "Elaborazione..." : "Pronto"}
        </div>
      </div>

      {/* Engine switcher */}
      <div className="mt-4 inline-flex p-1 rounded-full bg-[#F0EBE1] gap-1">
        <button
          data-testid="stt-engine-whisper"
          onClick={() => !listening && setEngine("whisper")}
          className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold transition-all ${engine === "whisper" ? "bg-white text-[#C85A32] shadow-sm" : "text-[#6B625B]"}`}
        >
          <Cpu size={12}/> Whisper AI
        </button>
        <button
          data-testid="stt-engine-browser"
          onClick={() => !listening && setEngine("browser")}
          disabled={!browserSupported}
          className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold transition-all ${engine === "browser" ? "bg-white text-[#C85A32] shadow-sm" : "text-[#6B625B]"} ${!browserSupported && "opacity-40"}`}
        >
          <Chrome size={12}/> Browser
        </button>
      </div>
      <div className="mt-2 text-[11px] text-[#6B625B]">
        {engine === "whisper"
          ? "Whisper: massima precisione anche con accenti e rumore. Registra e trascrivi in un click."
          : "Web Speech del browser: trascrizione live gratuita e istantanea (dipende dal browser)."}
      </div>

      <div className="mt-6 flex flex-col items-center">
        <button
          data-testid="voice-record-btn"
          onClick={listening ? stop : start}
          disabled={processing || (engine === "browser" && !browserSupported)}
          className={`w-28 h-28 rounded-full grid place-items-center transition-all ${
            listening ? "bg-[#E05328] text-white mic-pulse" : "bg-[#C85A32] hover:bg-[#B24E2A] text-white"
          } ${processing && "opacity-70"}`}
        >
          {processing ? <Loader2 className="animate-spin" size={38}/> : listening ? <Square size={34}/> : <Mic size={38}/>}
        </button>

        <div className="mt-5 h-8 flex items-center gap-1.5">
          {listening ? [...Array(9)].map((_, i) => (
            <div key={i} className="w-1.5 bg-[#C85A32] rounded-full wave-bar" style={{ animationDelay: `${i * 0.08}s`, height: "24px" }}/>
          )) : (
            <div className="text-xs text-[#6B625B]">
              Prova: "Aggiungi Spaghetti alle Vongole a 15 euro nei Primi"
            </div>
          )}
        </div>

        {transcript && (
          <div className="mt-4 w-full bg-[#FBF9F5] border border-[#E8E2D5] rounded-xl p-4 fade-in-up">
            <div className="text-xs uppercase tracking-wider text-[#6B625B] font-semibold mb-1">Trascrizione</div>
            <div className="text-[#231F1D] italic">"{transcript}"</div>
            {!listening && !processing && (
              <button data-testid="voice-parse-btn" onClick={() => parse(transcript)} className="mt-3 bg-[#3B503A] hover:bg-[#2f4030] text-white text-sm font-semibold rounded-full px-4 py-2">
                Analizza con AI
              </button>
            )}
          </div>
        )}
      </div>

      <div className="mt-6 border-t border-[#F0EBE1] pt-4">
        <div className="text-xs uppercase tracking-wider text-[#6B625B] font-semibold mb-2">Oppure scrivi il comando</div>
        <div className="flex gap-2">
          <input
            data-testid="voice-manual-input"
            value={manualText}
            onChange={(e) => setManualText(e.target.value)}
            placeholder="Aggiungi Tiramisu a 6 euro nei Dessert"
            className="flex-1 rounded-full border border-[#E8E2D5] bg-white px-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#C85A32]"
          />
          <button data-testid="voice-manual-parse-btn" onClick={() => parse(manualText)} disabled={processing || !manualText.trim()} className="bg-[#C85A32] hover:bg-[#B24E2A] disabled:opacity-50 text-white text-sm font-semibold rounded-full px-5">
            Analizza
          </button>
        </div>
      </div>
    </div>
  );
}
